import hotToast, {
  Toaster as HotToaster,
  ToastPosition,
} from "react-hot-toast";
import { Button } from "../Button";
import { Body } from "../Typography";
import "./Toast.css";
import { Icon } from "../Icons";

const DEFAULT_DURATION = 4000;
const DEFAULT_POSITION = "top-center";
export interface ToasterProps {
  /**
   * Position of the toast notifications
   * @default "top-center"
   */
  position?: ToastPosition;
  /**
   * Duration in milliseconds before toast auto-closes
   * @default 4000
   */
  duration?: number;
  /**
   * Custom class name for the toaster container
   */
  className?: string;
}

export interface ToastOptions {
  /**
   * Toast message — can be a string or React node (e.g. JSX with icons)
   */
  message: React.ReactNode;
  /**
   * Optional description
   */
  description?: string;
  /**
   * Toast variant
   * @default "default"
   */
  variant?: "default" | "success" | "error";
  /**
   * Optional action button
   */
  action?: {
    label: string;
    onClick: () => void;
    /**
     * Whether the toast should auto-dismiss after the action is invoked.
     * Default true (preserves existing behavior). Set false when the
     * action opens a modal the user may close back to the toast.
     */
    dismissOnAction?: boolean;
  };
  /**
   * Duration in milliseconds before toast auto-closes
   * Set to Infinity to keep open until manually closed
   * @default 4000
   */
  duration?: number;
  /**
   * Position of this specific toast
   * Overrides the global Toaster position
   */
  position?: ToastPosition;
}

interface CustomToastProps extends ToastOptions {
  id: string;
  visible: boolean;
}

/**
 * Toaster component - Add this once to your app root to enable toast notifications
 *
 * @example
 * ```tsx
 * import { Toaster } from '@indigo-components';
 *
 * function App() {
 *   return (
 *     <>
 *       <Toaster />
 *       {/* rest of your app *\/}
 *     </>
 *   );
 * }
 * ```
 */
export const Toaster: React.FC<ToasterProps> = ({
  position = DEFAULT_POSITION,
  duration = DEFAULT_DURATION,
  className,
}) => {
  return (
    <HotToaster
      position={position}
      containerClassName={className}
      toastOptions={{
        duration,
        custom: {
          style: {
            background: "transparent",
            padding: 0,
            boxShadow: "none",
            maxWidth: "none",
          },
        },
      }}
    />
  );
};

Toaster.displayName = "Toaster";

/**
 * Custom Toast component with full control over layout and styling
 */
function CustomToast(props: CustomToastProps) {
  const {
    id,
    message,
    description,
    variant = "default",
    action,
    visible,
  } = props;

  // Variant-specific background colors
  const variantClasses = {
    default: "indigo-toast-default",
    success: "indigo-toast-success",
    error: "indigo-toast-error",
  };

  return (
    <div
      className={`indigo-toast ${variantClasses[variant]} ${visible ? "indigo-toast-enter" : "indigo-toast-exit"}`}
    >
      {/* Message content */}
      <div className="indigo-toast-content">
        <Body className="indigo-toast-message text-indigo-gray-0">
          {message}
        </Body>
        {description && (
          <Body className="indigo-toast-description font-normal text-indigo-gray-0">
            {description}
          </Body>
        )}
      </div>

      {/* Action button (if provided) */}
      {action && (
        <Button
          size="md"
          variant="link"
          className="indigo-toast-action text-indigo-gray-0"
          onClick={() => {
            action.onClick();
            if (action.dismissOnAction !== false) {
              hotToast.dismiss(id);
            }
          }}
        >
          {action.label}
        </Button>
      )}

      {/* Close button */}
      <button
        className="indigo-toast-close"
        onClick={() => hotToast.dismiss(id)}
        aria-label="Close"
      >
        <Icon
          name="close"
          size="s"
          className="indigo-toast-close-icon flex-shrink-0"
        />
      </button>
    </div>
  );
}

/**
 * Toast API - Call from anywhere in your app to show toast notifications
 *
 * @example
 * ```tsx
 * import { toast } from '@indigo-components';
 *
 * // Basic usage
 * toast({ message: 'Operation completed' });
 *
 * // With variants
 * toast({ message: 'Changes saved successfully', variant: 'success' });
 * toast({ message: 'Failed to save changes', variant: 'error' });
 *
 * // With description
 * toast({
 *   message: 'Changes saved',
 *   description: 'Your changes have been saved successfully.',
 *   variant: 'success'
 * });
 *
 * // With action button
 * toast({
 *   message: 'Failed to save changes',
 *   variant: 'error',
 *   action: {
 *     label: 'Retry',
 *     onClick: () => handleRetry()
 *   }
 * });
 *
 * // With custom duration
 * toast({ message: 'Copied to clipboard', duration: 2000 });
 *
 * // Keep open until manually closed
 * toast({ message: 'Important notice', duration: Infinity });
 *
 * // Convenience methods
 * toast.success('Changes saved successfully');
 * toast.error('Failed to save changes');
 * ```
 */
export function toast(options: ToastOptions) {
  return hotToast.custom(
    (t) => <CustomToast id={t.id} visible={t.visible} {...options} />,
    {
      duration: options.duration,
      position: options.position,
    },
  );
}

// Convenience methods for common variants

// Note: use default variant for success
toast.success = (message: React.ReactNode, options?: Partial<ToastOptions>) => {
  return toast({ message, ...options });
};

toast.error = (message: React.ReactNode, options?: Partial<ToastOptions>) => {
  return toast({ message, variant: "error", ...options });
};

toast.dismiss = (toastId?: string) => hotToast.dismiss(toastId);
